import { useRef } from "react"

import {
  marketingClient,
  orderClient,
  useMutation,
  useQueryClient,
} from "@trueskin-web/apis"
import { getVoucherErrorType } from "@trueskin-web/functions"

import useCurrentPresets from "../../../hooks/use-current-presets"

const withVoucherActions = (Component) => (props) => {
  const queryClient = useQueryClient()

  const { data: plan } = queryClient.getQueryData("currentOrderSummary")

  const { voucher, discountGroupVariant } =
    plan.vouchers.find(
      ({ used, systemDefault, voucher: { stackable } = {} }) =>
        used && !(systemDefault || stackable)
    ) || {}

  const voucherDetails = voucher
    ? {
        code: voucher.code,
        message: discountGroupVariant.definition.confirmationMessage,
      }
    : null

  const referralDetails =
    plan.referral && plan.referral.used ? plan.referral : null

  return (
    <CurrentPresetVoucherActions
      component={Component}
      isFeatureAvailable={plan.occurrence !== "occ_once"}
      voucher={referralDetails ?? voucherDetails}
      {...props}
    />
  )
}

const CurrentPresetVoucherActions = ({
  component: Component,
  isFeatureAvailable,
  voucher,
  ...props
}) => {
  const voucherRef = useRef(null)
  const queryClient = useQueryClient()

  const { data: { data: presets } = {} } = useCurrentPresets()

  const recurringPlanId = presets?.find(
    ({ isRecommended }) => isRecommended
  )?.id

  const { mutateAsync: setCurrentPreset } = useMutation(
    orderClient.setCurrentPreset,
    {
      onSuccess: () =>
        (async () => {
          await Promise.all([
            queryClient.invalidateQueries("currentPresets"),
            queryClient.invalidateQueries("currentOrderSummary"),
          ])

          const { data } = queryClient.getQueryData("currentOrderSummary")

          const isAppliedVoucher = data.vouchers.some(
            (voucher) => voucher.used && !voucher.systemDefault
          )
          if (!isAppliedVoucher && voucherRef?.current) {
            voucherRef.current?.resetVoucherValidation()
          }
        })(),
    }
  )

  const { mutateAsync: addCurrentVoucher, variables: inputVoucher } =
    useMutation(orderClient.addCurrentVoucher, {
      onSuccess: ({ data: { voucher, order } }) =>
        (async (voucher, order) => {
          const { code: voucherCode, discountGroupId: discountGroup } = voucher
          const { customer: userId, preset: presetId } = order

          await Promise.all([
            queryClient.invalidateQueries("currentPresets"),
            queryClient.invalidateQueries("currentOrderSummary"),
          ])

          await Promise.all([
            marketingClient.sendEvent({
              name: marketingClient.EVENT_TYPES_ENUM.VOUCHER_ADDED,
              data: {
                voucherCode,
                userId,
                discountGroup,
                presetId,
                voucherApplication: "manual",
              },
            }),
          ])
        })(voucher, order),
    })

  const { mutateAsync: removeCurrentVoucher } = useMutation(
    orderClient.removeCurrentVoucher,
    {
      onSuccess: () =>
        (async () => {
          await Promise.all([
            queryClient.invalidateQueries("currentPresets"),
            queryClient.invalidateQueries("currentOrderSummary"),
          ])
        })(),
    }
  )

  const addVoucherHandler = (...args) => {
    if (!isFeatureAvailable) {
      const message = "VOUCHER_NOT_AUTOREFILL"

      marketingClient.sendEvent({
        name: marketingClient.EVENT_TYPES_ENUM.ADD_VOUCHER_ERROR,
        data: { errorType: getVoucherErrorType(message) },
      })
      return Promise.reject({
        message,
        action: () => setCurrentPreset(recurringPlanId),
        voucher: inputVoucher,
      })
    }

    return addCurrentVoucher(...args)
  }

  return (
    <Component
      ref={voucherRef}
      voucher={voucher}
      addVoucher={addVoucherHandler}
      removeVoucher={removeCurrentVoucher}
      {...props}
    />
  )
}

export default withVoucherActions
